import React, {useCallback, useLayoutEffect, useState} from "react";
import {connect} from "react-redux";
import memoizeOne from "memoize-one";
import _ from 'lodash';
import {FixedSizeList, areEqual} from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import {LotStateEnum, RoundStopTypeEnum, RoundTypeEnum} from "@nextlot/core/webcast/data/WebcastDataDomain";
import {TaggedLogger} from "@nextlot/core/utilities";
import type {Lot, Round} from "@nextlot/core/webcast/data/data_types_definitions";
import {formatCurrency} from "../CurrencyAmountDisplay";
import BidderLotDetailsCmp from "./BidderLotDetailsCmp";
import {useTranslation} from "react-i18next";
import {ReduxStateType} from "@nextlot/core/webcast/services/DataStore";
import useIntersectionObserver from '@react-hook/intersection-observer';
import {AuctionsManager} from "@nextlot/core/webcast";
import classNames from "classnames";
import {ParticipantRolesEnum} from "@nextlot/core/webcast/services/AuctionsManager";


const _logger = TaggedLogger.get('BidderCatalogLotsListCmp');

type DerivedLot = Lot & {
    stateDisplay: string,

    isPartOfCurrentRound?: boolean,
    soldPriceValueCents?: number,
    isWatched: boolean,
    isLoading: boolean,
}

type LotsListItemDataType = {
    lots: Array<DerivedLot>,
    onClickLotsListItem: (itemIndex: number) => () => void,
    onClickLotWatched: (itemIndex: number) => () => void,
}

const LOTS_LIST_ITEM_HEIGHT: number = 90; // has to match scss .bidder-catalog-and-purchases-outline>.bidder-lots-list-container>.lots-list-item-container


// React.memo acts as a PureComponent here (https://reactjs.org/blog/2018/10/23/react-v-16-6.html#reactmemo)
function LotListItemMemoCmp(props: { index: number, style: {}, data: LotsListItemDataType }) {
    const {index, style} = props;
    const itemData: LotsListItemDataType = props.data;
    const lot: DerivedLot = itemData.lots[index];
    const handleClick_LotListItem = itemData.onClickLotsListItem(index);
    const handleClick_LotWatched = itemData.onClickLotWatched(index);
    const participantIsBidder = AuctionsManager.currentWebcastAuctionHandler.auctionConnectData.participantRole === ParticipantRolesEnum.bidder

    return (
        <div className="list-item-wrapper" style={style}>

            {/* FIXME had to reset/set cursor styles below via `style` attributes (with no available utility classes), to avoid recompiling themes */}
            <div className={classNames('list-item-container', {'in-current-round': lot.isPartOfCurrentRound, 'is-watched': lot.isWatched})}
                 style={{ cursor: 'initial' }}>

                <img src={lot.defaultImageSmUrl}
                     alt={`Lot #${lot.number} default photo`}
                     style={{ cursor: 'pointer' }}
                     onClick={handleClick_LotListItem} />

                <div className="text-container">
                    <div className="lot-number-and-state-line">
                        <span>
                            <span className="lot-number">#<strong>{lot.number}</strong></span>
                            { participantIsBidder && (
                                lot.isWatched

                                    ? <span className={classNames("lot-watched text-danger ms-2", { 'text-muted': lot.isLoading })}
                                            style={{ cursor: lot.isLoading ? 'progress' : 'pointer' }}
                                            onClick={handleClick_LotWatched}>★ Watched</span>

                                    : <span className={classNames("lot-watched text-secondary ms-2", { 'text-muted': lot.isLoading })}
                                            style={{ cursor: lot.isLoading ? 'progress' : 'pointer' }}
                                            onClick={handleClick_LotWatched}>☆ Watch</span>
                            )}
                        </span>
                        <span className="lot-state">{lot.isPartOfCurrentRound ? '\u00bb' : ''} {lot.stateDisplay}</span>
                    </div>


                    <div className="lot-name"
                         style={{ cursor: 'pointer' }}
                         onClick={handleClick_LotListItem}>{lot.name}</div>
                </div>
            </div>

            <div className="item-vertical-spacer-bottom"/>
        </div>
    )
}

const LotListItemRenderer = React.memo(LotListItemMemoCmp, areEqual);


const createLotsListItemData = memoizeOne((lots, currentRoundLotsIds, watchedLotsIds, loadingLotsIds, roundBiddingIsStarted, closedRounds, onClickLotsListItem, onClickLotWatched, t) => {

    const soldRounds: Array<Round> = closedRounds.filter((round: Round) => round.stopType === RoundStopTypeEnum.SOLD);

    const LOT_STATES_DISPLAY_MAP = new Map([
        [LotStateEnum.OPEN as number, ''],
        [LotStateEnum.CLOSED_NO_SALE as number, `${t('catalogAndMyPurchases.passed')}`],
        [LotStateEnum.CLOSED_PENDING as number, `${t('catalogAndMyPurchases.pending')}`],
        [LotStateEnum.CLOSED_SOLD as number, `${t('catalogAndMyPurchases.sold')}`],
    ]);

    const derivedLots = lots.map((lot: Lot) => {
        const derivedLot: DerivedLot = {
            ...lot,
            stateDisplay: LOT_STATES_DISPLAY_MAP.get(lot.state),
            isWatched: watchedLotsIds.includes(lot.id),
            isLoading: loadingLotsIds.includes(lot.id),
        };

        const soldRound: Round = _.findLast(soldRounds, (r: Round) => r.lotsIds.includes(lot.id));
        if (soldRound) {
            derivedLot.soldPriceValueCents = soldRound.highestBid.value;
            if (soldRound.roundType === RoundTypeEnum.SINGLE_BOXLOT) {
                derivedLot.stateDisplay += ` ${t('common.for')} ` + formatCurrency(soldRound.highestBid.value) + ` x ${lot.itemsQty} = ${formatCurrency(soldRound.highestBid.value * lot.itemsQty)}` ;
            } else {
                derivedLot.stateDisplay += ` ${t('common.for')} ` + formatCurrency(soldRound.highestBid.value);
            }
        }

        if (currentRoundLotsIds.includes(lot.id)) {
            derivedLot.stateDisplay = roundBiddingIsStarted ? `${t('catalogAndMyPurchases.biddingNow')}` : `${t('catalogAndMyPurchases.upNext')}`;
            derivedLot.isPartOfCurrentRound = true;
        }


        return derivedLot;
    });


    return {
        lots: derivedLots,
        onClickLotsListItem,
        onClickLotWatched,
    }
});


function BidderCatalogLotsListCmp(props) {
    // _logger.debug('.render()', props);

    const { t, i18n } = useTranslation();

    const refCatalogLotsList = React.useRef<HTMLDivElement>(null);
    const refLotsFixedSizeList = React.useRef<FixedSizeList>(null);

    const [selectedLot, setSelectedLot] = React.useState<Lot>(null);
    const [allowTrackLots, setAllowTrackLots] = React.useState<boolean>(false);
    const [loadingLotsIds, setLoadingLotsIds] = React.useState([]);

    const {isIntersecting} = useIntersectionObserver<HTMLDivElement>(refCatalogLotsList, {threshold: 0.25});


    React.useEffect(() => {
      scrollLotListToCurrentItem();
    }, [isIntersecting, allowTrackLots, props.currentRoundLotsIds]);

    const scrollLotListToCurrentItem = () => {
        if (allowTrackLots) {
            const lots: Array<Lot> = props.webcastAuctionData.lots;
            const currentRoundLotsIds: Array<number> = props.currentRoundLotsIds;

            lots.map(lot => {
                if (currentRoundLotsIds.includes(lot.id)) {
                    if (refLotsFixedSizeList.current && (currentRoundLotsIds[0] === lot.id)) {
                        refLotsFixedSizeList.current.scrollToItem(lots.indexOf(lot), "start");
                    }
                }
            })
        }
    };


    const handleClick_LotsListItem = (itemIndex: number) => () => {
        const lot: Lot = props.webcastAuctionData.lots[itemIndex];
        setSelectedLot(lot);
    };

    const handleClick_LotWatched = (itemIndex: number) => () => {
        const lot = props.webcastAuctionData.lots[itemIndex];
        const isWatched = props.webcastAuctionData.watchedLotsIds.includes(lot.id);
        const isLoading = loadingLotsIds.includes(lot.id);
        if (!isLoading) {
            setLoadingLotsIds([...loadingLotsIds, lot.id]);
            AuctionsManager.currentWebcastAuctionHandler.controller.submitLotInterest(lot.id, !isWatched).finally(() => {
                setLoadingLotsIds(loadingLotsIds.filter(id => id !== lot.id));
            });
        }
    };

    const handleClick_BackToList = (evt) => {
        evt.preventDefault();

        setSelectedLot(null);
    };

    const handleChange_TrackLots = () => {
        setAllowTrackLots(!allowTrackLots);
    };


    const itemData: LotsListItemDataType = createLotsListItemData(
        props.webcastAuctionData.lots,
        props.currentRoundLotsIds,
        props.webcastAuctionData.watchedLotsIds,
        loadingLotsIds,
        props.roundBiddingIsStarted,
        props.webcastAuctionData.closedRounds,
        handleClick_LotsListItem,
        handleClick_LotWatched,
        t
    );


    return (
        <div className="cmp__body" ref={refCatalogLotsList}>
            <div className="flex-grow-1">
                <AutoSizer>
                    {({height, width}) => (
                        <FixedSizeList
                            className="bidder-lots-list-window-container"
                            height={height}
                            width={width}
                            itemCount={props.webcastAuctionData.lots.length}
                            itemSize={LOTS_LIST_ITEM_HEIGHT}
                            itemData={itemData}
                            itemKey={(index, itemData) => (itemData.lots[index].id)}
                            ref={refLotsFixedSizeList}
                        >

                            {LotListItemRenderer}

                        </FixedSizeList>
                    )}
                </AutoSizer>
            </div>

            <div className="ps-3 pt-2">
                <label className="align-middle">
                    <input type="checkbox" value={1} onChange={handleChange_TrackLots}
                           checked={allowTrackLots}/>
                    &nbsp;
                    {t('catalogAndMyPurchases.trackLots')}</label>
            </div>


            {
                selectedLot &&

                <div className="lot-details-container">
                    <div className="back-to-list-button-container">
                        <a href="#" onClick={handleClick_BackToList}>{'\u226a'} {t('catalogAndMyPurchases.backToLots')}</a>
                    </div>

                    <div className="details-body-wrapper">
                        <div className="details-body-scrollable-container">
                            <BidderLotDetailsCmp lot={selectedLot} photoGalleryAutoRotateEverySeconds={2} />
                        </div>
                    </div>
                </div>
            }
        </div>
    )
}


export default connect(({activeWebcast}: ReduxStateType) => {
    return {
        currentRoundLotsIds: activeWebcast.webcastCurrentRound.lotsIds,
        webcastAuctionData: activeWebcast.webcastAuctionData,
        roundBiddingIsStarted: (activeWebcast.webcastCurrentRoundBidding.askingBidValue > 0),
    };
})(BidderCatalogLotsListCmp);



// type DerivedLot = Lot & {
//     stateDisplay: string,
//
//     isPartOfCurrentRound?: boolean,
//     soldPriceValueCents?: number,
// }
//
// type LotsListItemDataType = {
//     lots: Array<DerivedLot>,
//     onClickLotsListItem: (itemIndex: number)=>void,
// }
//
// const LOTS_LIST_ITEM_HEIGHT: number = 90; // has to match scss .bidder-catalog-and-purchases-outline>.bidder-lots-list-container>.lots-list-item-container
//
// // React.memo acts as a PureComponent here (https://reactjs.org/blog/2018/10/23/react-v-16-6.html#reactmemo)
// const LotListItemRenderer = React.memo(function LotListItemMemoCmp(props: { index: number, style: {}, data: LotsListItemDataType }) {
//     const {index, style} = props;
//     const itemData: LotsListItemDataType = props.data;
//     const lot: DerivedLot = itemData.lots[index];
//
//     return (
//         <div className="list-item-wrapper" style={style}>
//
//             <div className={`list-item-container ${lot.isPartOfCurrentRound ? 'in-current-round' : ''}`}
//                  onClick={itemData.onClickLotsListItem(index)}>
//
//                 <img src={lot.defaultImageSmUrl} alt={`Lot #${lot.displayLabel} default photo`}/>
//
//                 <div className="text-container">
//                     <div className="lot-number-and-state-line">
//                         <span className="lot-number">#<strong>{lot.displayLabel}</strong></span>
//                         <span className="lot-state">{lot.isPartOfCurrentRound ? '\u00bb' : ''} {lot.stateDisplay}</span>
//                     </div>
//
//
//                     <div className="lot-name">{lot.name}</div>
//                 </div>
//             </div>
//
//             <div className="item-vertical-spacer-bottom"/>
//         </div>
//     )
// }, areEqual);
//
//
// const createLotsListItemData: LotsListItemDataType = memoizeOne(
//     (lots: Array<Lot>, currentRoundLotsIds: Array<number>, roundBiddingIsStarted: boolean, closedRounds: Array<Round>, onClickLotsListItem: ()=>void, t): LotsListItemDataType => {
//
//         const soldRounds: Array<Round> = closedRounds.filter((round: Round) => round.stopType === RoundStopTypeEnum.SOLD);
//
//         const LOT_STATES_DISPLAY_MAP = new Map([
//             [LotStateEnum.OPEN, ''],
//             [LotStateEnum.CLOSED_NO_SALE, `${t('catalogAndMyPurchases.passed')}`],
//             [LotStateEnum.CLOSED_PENDING, `${t('catalogAndMyPurchases.pending')}`],
//             [LotStateEnum.CLOSED_SOLD, `${t('catalogAndMyPurchases.sold')}`],
//         ]);
//
//         const derivedLots = lots.map((lot: Lot) => {
//             const derivedLot: DerivedLot = {
//                 ...lot,
//                 stateDisplay: LOT_STATES_DISPLAY_MAP.get(lot.state),
//             };
//
//             const soldRound: ?Round = _.findLast(soldRounds, (r: Round) => r.lotsIds.includes(lot.id));
//             if (soldRound) {
//                 derivedLot.soldPriceValueCents = soldRound.highestBid.value;
//                 derivedLot.stateDisplay += ` ${t('common.for')} ` + formatCurrency(soldRound.highestBid.value);
//             }
//
//             if (currentRoundLotsIds.includes(lot.id)) {
//                 derivedLot.stateDisplay = roundBiddingIsStarted ? `${t('catalogAndMyPurchases.biddingNow')}` : `${t('catalogAndMyPurchases.upNext')}`;
//                 derivedLot.isPartOfCurrentRound = true;
//             }
//
//
//             return derivedLot;
//         });
//
//
//         return {
//             lots: derivedLots,
//             onClickLotsListItem
//         }
//     });
//
//
// const _logger = TaggedLogger.get('BidderCatalogLotsListCmp');
//
//
// type CmpPropsType = {
//     webcastAuctionData: ReduxWebcastAuctionDataType,
//     currentRoundLotsIds: ?Array<number>,
//     roundBiddingIsStarted: boolean,
// };
//
//
// type CmpStateType = {
//     selectedLot: ?Lot,
//     allowTrackLots: boolean,
// }
//
//
// class BidderCatalogLotsListCmp extends React.PureComponent<CmpPropsType, CmpStateType> {
//
//
//     constructor(props) {
//         super(props);
//
//         this.refLotsFixedSizeList = React.createRef();
//
//         this.state = {
//             selectedLot: null,
//             allowTrackLots: false
//         };
//
//     }
//
//     componentDidUpdate(): void {
//         if (this.state.allowTrackLots) {
//             this.scrollLotListToCurrentItem();
//         }
//     }
//
//
//     scrollLotListToCurrentItem = () => {
//         const lots: Array<Lot> = this.props.webcastAuctionData.lots;
//         const currentRoundLotsIds: Array<number> = this.props.currentRoundLotsIds;
//
//         lots.map(lot => {
//             if (currentRoundLotsIds.includes(lot.id)) {
//                 if (currentRoundLotsIds[0] === lot.id) {
//                     this.refLotsFixedSizeList.current.scrollToItem(lots.indexOf(lot), "start");
//                 }
//             }
//         })
//     };
//
//     handleClick_LotsListItem = (itemIndex: number) => () => {
//         const lot: Lot = this.props.webcastAuctionData.lots[itemIndex];
//
//         this.setState({
//             selectedLot: lot
//         });
//     };
//
//
//     handleClick_BackToList = (evt) => {
//         evt.preventDefault();
//
//         this.setState({
//             selectedLot: null,
//         })
//     };
//
//     handleChange_TrackLots = () => {
//         this.setState({
//             allowTrackLots: !this.state.allowTrackLots
//         });
//     };
//
//
//     render() {
//         _logger.debug('.render()', this.props);
//         const {t} = this.props;
//
//         const webcastAuctionData: ReduxWebcastAuctionDataType = this.props.webcastAuctionData;
//         const lots: Array<Lot> = webcastAuctionData.lots;
//         const selectedLot: Lot = this.state.selectedLot;
//
//
//         const itemData: LotsListItemDataType = createLotsListItemData(
//             lots,
//             this.props.currentRoundLotsIds,
//             this.props.roundBiddingIsStarted,
//             webcastAuctionData.closedRounds,
//             this.handleClick_LotsListItem,
//             t
//         );
//
//         return (
//             <div className="cmp__body">
//                 <div className="flex-grow-1">
//                     <AutoSizer>
//                         {({height, width}) => (
//                             <FixedSizeList
//                                 className="bidder-lots-list-window-container"
//                                 height={height}
//                                 width={width}
//                                 itemCount={lots.length}
//                                 itemSize={LOTS_LIST_ITEM_HEIGHT}
//                                 itemData={itemData}
//                                 itemKey={(index, itemData) => (itemData.lots[index].id)}
//                                 ref={this.refLotsFixedSizeList}
//                             >
//
//                                 {LotListItemRenderer}
//
//                             </FixedSizeList>
//                         )}
//                     </AutoSizer>
//                 </div>
//
//                 <div className="ps-3 pt-2">
//                     <label className="align-middle">
//                         <input type="checkbox" value={1} onChange={this.handleChange_TrackLots}
//                                checked={this.state.allowTrackLots}/>
//                         &nbsp;
//                         {t('catalogAndMyPurchases.trackLots')}</label>
//                 </div>
//
//
//                 {
//                     selectedLot &&
//
//                     <div className="lot-details-container">
//                         <div className="back-to-list-button-container">
//                             <a href="#" onClick={this.handleClick_BackToList}>{'\u226a'} {t('catalogAndMyPurchases.backToLots')}</a>
//                         </div>
//
//                         <div className="details-body-wrapper">
//                             <div className="details-body-scrollable-container">
//                                 <BidderLotDetailsCmp lot={selectedLot} photoGalleryAutoRotateEverySeconds={2}/>
//                             </div>
//                         </div>
//                     </div>
//                 }
//             </div>
//         );
//     }
// }
//
//
// export default compose(
//     connect(({activeWebcast}) => {
//         return {
//             currentRoundLotsIds: activeWebcast.webcastCurrentRound.lotsIds,
//             webcastAuctionData: activeWebcast.webcastAuctionData,
//             roundBiddingIsStarted: (activeWebcast.webcastCurrentRoundBidding.askingBidValue > 0),
//         }
//     }),
//     withTranslation()
// )(BidderCatalogLotsListCmp)
